/* ============================================================================
   Stickr Lab — Builder telemetry  (assets/sl-builder-telemetry.js)

   Phase 1 of Builder Analytics: emit builder events. Nothing else.

   ── THE ONE RULE ─────────────────────────────────────────────────────────
   THE BUILDER MUST NEVER NOTICE THIS FILE EXISTS.

   No customer action waits on telemetry. No telemetry failure reaches the
   builder. Every entry point is wrapped, every listener is passive, delivery
   is `sendBeacon` and is never retried. If this file throws on line one, the
   builder still uploads, prices, adds to cart and checks out exactly as it
   did before.

   That is not a style preference. A dropped analytics event costs a row in a
   dashboard; a builder that will not add to cart costs the order.

   ── WHY IT DOES NOT TOUCH premium-builder.js ─────────────────────────────
   That bundle is 180KB of precompiled JSX and its `.pb-*` class names are the
   QA screenshot contract (BUILDER-DESIGN-LOCK-HANDOFF). Editing it to sprinkle
   tracking calls would put analytics inside the highest-risk file in the
   repository and make every future rebuild a merge.

   So this observes from OUTSIDE, using only things that already exist:
     · window error events            -> javascript_error
     · a fetch wrapper                -> api_error, add_to_cart, checkout
     · Navigation Timing              -> builder_load_time
     · delegated clicks on .pb-*      -> editing + configuration events
     · a change listener on file input-> upload_started

   It also publishes `window.slTrack(event, fields)` so the builder can emit
   richer events later without this file guessing at the DOM. Nothing calls it
   today; it is the seam for when the bundle is next rebuilt.

   ── SHIPS DARK ───────────────────────────────────────────────────────────
   `enabled` defaults to FALSE. Phase 1 lands the instrumentation and the kill
   switch; Phase 2 builds the collector and only then is it turned on. With no
   endpoint configured nothing is ever sent, and the queue is capped so a
   forgotten flag cannot grow memory.

   ── WHAT IS NEVER COLLECTED ──────────────────────────────────────────────
   Artwork, filenames, image bytes, pixel dimensions, names, emails,
   addresses, IP, user agent, referrer, cart tokens, or any free text.
   Error CODES only, never messages or stacks — a stack trace is the most
   likely accidental carrier of customer data.
   ============================================================================ */
(function (w, d) {
  'use strict';

  /* Everything below runs inside this guard. If anything at all goes wrong
     while setting up, telemetry simply does not exist and the builder is
     untouched. */
  try {

    var SCHEMA_VERSION = 1;

    var CFG = w.SL_TELEMETRY || {};
    var cfg = {
      /* KILL SWITCH. Off until Phase 2 ships the collector. */
      enabled: CFG.enabled === true,
      endpoint: typeof CFG.endpoint === 'string' ? CFG.endpoint : '',
      /* Caps. A runaway loop must not flood the collector or the tab. */
      maxEventsPerSession: 100,
      maxQueue: 40,
      flushIntervalMs: 5000,
      idleExpiryMs: 30 * 60 * 1000,
      debug: CFG.debug === true
    };

    /* ---- the safe enable path -------------------------------------------
       Turn-on must be ONE flip, and that flip must not be able to do damage.

       The endpoint is staged FIRST, while dark. With `enabled` false it is
       completely inert, so the collector URL can be set, deployed and read
       back at leisure with nothing leaving a single tab. After that the
       checkbox is the only control anyone ever touches — and turning it back
       off stops collection everywhere with no deploy.

       Both halves are still required to transmit, and the endpoint must be
       HTTPS. A theme setting is editable by anyone holding Shopify admin; if
       one were ever pointed somewhere it should not be, `http:` would put
       every event on the wire in clear text. Refusing anything but https
       costs a legitimate collector nothing.

       `enabled` is compared with === true, so a setting that renders as the
       STRING "true" — a broken Liquid edit, a hand-written config — leaves
       telemetry off. Ambiguity resolves to dark. */
    function validEndpoint(url) {
      try {
        if (typeof url !== 'string' || !url) return false;
        if (url.length > 300) return false;
        return /^https:\/\/[^\s"'<>\\]+$/i.test(url);
      } catch (e) { return false; }
    }

    var ENDPOINT_OK = validEndpoint(cfg.endpoint);

    /* The single source of truth for "is anything actually transmitted".
       Every send path asks this one question. */
    function live() { return !!(cfg.enabled && ENDPOINT_OK); }

    /* ---- the event vocabulary, validated before anything is queued ------
       An event not on this list is DROPPED. The collector validates again;
       this is the cheap first gate so malformed events never leave the tab. */
    var EVENTS = {
      builder_opened: 1, builder_closed: 1, builder_load_time: 1,
      upload_started: 1, upload_completed: 1, upload_failed: 1,
      crop_changed: 1, zoom_changed: 1, rotate_changed: 1,
      reset_pressed: 1, undo_pressed: 1,
      size_selected: 1, price_viewed: 1,
      add_to_cart: 1, checkout_started: 1,
      javascript_error: 1, api_error: 1, timeout_error: 1,
      interaction_latency: 1, upload_duration: 1,

      /* ---- THE TWO BUSINESS OUTCOMES -----------------------------------
         Everything above this line records something the customer DID, or
         something the page observed. These two record whether the thing they
         were trying to do actually happened, and they are the only events in
         the vocabulary that count the same population on every device and
         every path.

         `artwork_available`  the builder is holding displayable customer
                              artwork. Not "a file was chosen", not "IndexedDB
                              stored a blob" — the picture is in the builder.
         `cart_add_completed` Shopify accepted the cart mutation. Not the
                              button, not the dialog, not the attempt.

         They exist because the funnel could not be read without them:
         `upload_completed` fires only on the file-input path, and
         `add_to_cart` fires only from the desktop tree and only marks a
         dialog OPENING. Both stages were therefore uncountable. */
      artwork_available: 1, cart_add_completed: 1
    };

    /* Only these keys may ride along, and only with these types. Anything
       else is stripped — an allow-list, so a future careless caller cannot
       leak a filename by adding a field. */
    var FIELDS = {
      fileType: 'string', fileSizeBucket: 'string', reason: 'string',
      attemptNo: 'number', durationMs: 'number', ms: 'number',
      widthIn: 'number', heightIn: 'number', sizeMode: 'string',
      quantity: 'number', priceCents: 'number',
      finish: 'string', material: 'string',
      changeCount: 'number', code: 'string',
      endpoint: 'string', httpStatus: 'number',
      /* The two dimensions that make an outcome comparable. Without them a
         stage counting every device sits above a stage counting one, and
         nothing on the row says so. Low cardinality and enumerated: they
         describe a FLOW, never a person, a design or a file. */
      surface: 'string', entryPath: 'string'
    };

    /* Free-text is never accepted. These are the only permitted values for
       the enum-ish fields, so an error code cannot become a message. */
    var ENUMS = {
      fileType: ['png', 'jpg', 'jpeg', 'svg', 'pdf', 'webp', 'other'],
      fileSizeBucket: ['<1mb', '1-5mb', '5-20mb', '>20mb'],
      reason: ['too_large', 'unsupported', 'resolution', 'timeout', 'server', 'aborted', 'unknown'],
      sizeMode: ['preset', 'custom'],
      code: ['script_error', 'promise_rejection', 'render_error', 'unknown'],
      endpoint: ['cart_add', 'pricing', 'other'],
      /* CF-1 A1.4 — frozen on the builder's canonical option IDs, not labels. */
      finish: ['matte', 'gloss', 'other'],
      material: ['vinyl', 'holo', 'clear', 'kraft', 'other'],
      /* Which BUILDER TREE rendered it. Deliberately NOT derived from the
         envelope's "device" field: that is a viewport bucket (<768 / <1180),
         and the builder switches trees at 820px — so an 800px viewport is
         stamped "tablet" while running the mobile builder. Inferring the
         surface from the viewport is exactly the mistake this field removes. */
      surface: ['desktop_builder', 'mobile_builder', 'other'],
      /* How the artwork reached the builder. "saved_design" is a RESUMED
         design rather than a new acquisition, and is named separately so it
         can never be silently folded into an upload count. */
      entryPath: ['direct_builder', 'homepage_handoff', 'saved_design', 'other']
    };

    /* A1.4 drift: these enums carry a neutral `other`, so an unrecognized but
       token-shaped CONTROLLED value drifts to `other` and the event survives
       rather than the field being dropped. Labels are NEVER aliased to an ID
       (`holographic` -> `other`, never `holo`); the builder emits canonical IDs.
       Free text / PII still fails SAFE_TOKEN and is dropped. */
    var DRIFT_TO_OTHER = { finish: 1, material: 1, surface: 1, entryPath: 1 };

    /* CF-1: no free text. Enumerated strings are pinned to their allow-list.
       Any OTHER allow-listed string must look like a TOKEN — no spaces, `@`,
       dots or slashes — so prose, paths and addresses are refused (an email is
       eighteen characters, under any length gate). SAFE_TOKEN also gates A1.4
       drift: an unknown value drifts to `other` only if it is token-shaped;
       anything else is dropped. */
    var SAFE_TOKEN = /^[a-z0-9][a-z0-9_-]{0,23}$/i;

    /* ---- per-event field contracts --------------------------------------
       FIELDS above says which keys may exist ANYWHERE. This says which keys
       mean anything on a GIVEN event, and it is the difference between a
       schema and a suggestion.

       Nine of the twenty events are DEFERRED SEAM events: this file cannot
       observe them from outside the bundle, so their only caller will ever be
       the builder itself calling `window.slTrack`. They are precisely the
       events with no compiler, no types, and no reviewer standing between a
       typo and the collector — so each one gets a signature, not a shared
       best guess. Without this, `slTrack('upload_failed', {priceCents:4199})`
       stores a priced upload failure and the UPLOADS model quietly inherits
       a number that means nothing.

       Emitted from outside (this file):  builder_opened · builder_closed ·
         builder_load_time · upload_started · crop_changed · zoom_changed ·
         add_to_cart · checkout_started · javascript_error · api_error ·
         interaction_latency
       DEFERRED to the seam (builder-emitted):  upload_completed ·
         upload_failed · upload_duration · rotate_changed · reset_pressed ·
         undo_pressed · size_selected · price_viewed · timeout_error */
    function set(keys) {
      var o = {}, a = keys ? keys.split(' ') : [];
      for (var i = 0; i < a.length; i++) if (a[i]) o[a[i]] = 1;
      return o;
    }

    var EVENT_FIELDS = {
      /* observed from outside */
      builder_opened:      set(''),
      builder_closed:      set(''),
      builder_load_time:   set('ms'),
      upload_started:      set('fileType fileSizeBucket attemptNo'),
      crop_changed:        set('changeCount'),
      zoom_changed:        set('changeCount'),
      add_to_cart:         set('priceCents quantity widthIn heightIn finish material sizeMode'),
      checkout_started:    set('priceCents quantity'),
      javascript_error:    set('code'),
      api_error:           set('endpoint httpStatus'),
      interaction_latency: set('ms'),
      /* deferred to the seam — builder-emitted, validated identically */
      upload_completed:    set('fileType fileSizeBucket attemptNo durationMs'),
      upload_failed:       set('fileType fileSizeBucket attemptNo reason'),
      upload_duration:     set('fileType fileSizeBucket durationMs'),
      rotate_changed:      set('changeCount'),
      reset_pressed:       set('changeCount'),
      undo_pressed:        set('changeCount'),
      size_selected:       set('widthIn heightIn sizeMode quantity'),
      price_viewed:        set('priceCents quantity widthIn heightIn finish material'),
      timeout_error:       set('endpoint ms durationMs'),
      /* Outcomes carry the two dimensions and NOTHING else. No price, no
         timing, no identifier: a KPI event that also carries diagnostics
         becomes a diagnostic nobody dares change. */
      artwork_available:   set('surface entryPath'),
      cart_add_completed:  set('surface entryPath')
    };

    /* A field without which the event cannot be read at all. Not "nice to
       have" — an `upload_duration` carrying no duration is a row of pure
       noise, and a `upload_failed` with no cause silently distorts the
       failure-share arithmetic that is computed downstream. */
    var REQUIRED = {
      builder_load_time:   ['ms'],
      interaction_latency: ['ms'],
      upload_duration:     ['durationMs'],
      upload_failed:       ['reason'],
      javascript_error:    ['code'],
      api_error:           ['endpoint'],
      timeout_error:       ['endpoint'],
      /* An outcome with no surface is an outcome that cannot be compared,
         which is the defect these events were added to end. Required on
         both, and defaulted below so the OUTCOME still counts. */
      artwork_available:   ['surface', 'entryPath'],
      cart_add_completed:  ['surface', 'entryPath']
    };

    /* Where the vocabulary already carries a neutral member, a missing cause
       becomes "uncategorised" rather than a lost event — the attempt still
       counts, which keeps the funnel total honest. Where there is no such
       member the event is dropped instead of invented. */
    var DEFAULTS = {
      upload_failed:    { reason: 'unknown' },
      javascript_error: { code: 'unknown' },
      api_error:        { endpoint: 'other' },
      timeout_error:    { endpoint: 'other' },
      artwork_available:  { surface: 'other', entryPath: 'other' },
      cart_add_completed: { surface: 'other', entryPath: 'other' }
    };

    /* ================================================================ session */

    var SESSION_KEY = 'sl_session_id';
    var SEEN_KEY = 'sl_session_seen';

    function randomId() {
      try {
        var a = new Uint8Array(16);
        (w.crypto || w.msCrypto).getRandomValues(a);
        var s = '';
        for (var i = 0; i < a.length; i++) s += (a[i] + 0x100).toString(16).slice(1);
        return s;
      } catch (e) {
        /* No crypto is not a reason to fall back to something weak and
           identifying — telemetry simply does not start. */
        return null;
      }
    }

    /**
     * A tab-scoped random id. Not a cookie, not derived from anything about
     * the customer, and it expires after 30 minutes idle.
     *
     * The SAME id is exposed for GA4 as the `sl_session_id` custom dimension,
     * which is what makes Landing Page -> Builder Loaded a real join rather
     * than an estimate.
     */
    function sessionId() {
      try {
        var now = Date.now();
        var id = w.sessionStorage.getItem(SESSION_KEY);
        var seen = parseInt(w.sessionStorage.getItem(SEEN_KEY) || '0', 10);
        if (id && seen && (now - seen) < cfg.idleExpiryMs) {
          w.sessionStorage.setItem(SEEN_KEY, String(now));
          return id;
        }
        id = randomId();
        if (!id) return null;
        w.sessionStorage.setItem(SESSION_KEY, id);
        w.sessionStorage.setItem(SEEN_KEY, String(now));
        return id;
      } catch (e) {
        /* Private browsing can throw on sessionStorage. Telemetry stops;
           the builder does not. */
        return null;
      }
    }

    var SID = sessionId();
    var seq = 0;
    var sent = 0;
    var queue = [];
    var timer = null;

    /* ============================================================== validate */

    function validate(name, fields) {
      if (!EVENTS[name]) return null;                // not in the vocabulary
      var allowed = EVENT_FIELDS[name];
      if (!allowed) return null;                     // vocabulary/contract drift

      /* Fields are validated into a holding object first. Nothing is queued
         and no sequence number is spent until the whole event is known good. */
      var kept = {};
      if (fields) {
        for (var k in fields) {
          if (!Object.prototype.hasOwnProperty.call(fields, k)) continue;
          if (!allowed[k]) continue;                 // not on THIS event -> stripped
          var want = FIELDS[k];
          if (!want) continue;                       // unknown key -> stripped
          var v = fields[k];
          if (typeof v !== want) continue;           // wrong type -> stripped
          if (want === 'number') {
            if (!isFinite(v)) continue;
            if (v < 0) continue;                     // counts, sizes, prices, durations
          }
          if (want === 'string') {
            if (ENUMS[k]) {
              var vv = v.toLowerCase();                            // A1.2: canonical IDs are lowercase
              if (ENUMS[k].indexOf(vv) !== -1) { v = vv; }         // a known ID (case-normalised)
              else if (DRIFT_TO_OTHER[k] && SAFE_TOKEN.test(v)) { v = 'other'; }  // A1.4: unknown controlled ID -> other
              else continue;                                       // label alias / free text / PII / non-drift miss -> stripped
            } else {
              if (v.length > 24) continue;             // no free text
              if (!SAFE_TOKEN.test(v)) continue;       // unenumerated -> token shape only
            }
          }
          kept[k] = v;
        }
      }

      /* A missing required field is not a field problem — it is an event that
         cannot be interpreted. Fill it from the neutral member if the
         vocabulary has one, otherwise drop the whole event. */
      var req = REQUIRED[name];
      if (req) {
        var def = DEFAULTS[name];
        for (var r = 0; r < req.length; r++) {
          var f = req[r];
          if (kept[f] !== undefined) continue;
          if (def && def[f] !== undefined) { kept[f] = def[f]; continue; }
          return null;
        }
      }

      /* The envelope is stamped LAST, so a dropped event never burns a
         sequence number. `seq` is half the collector's idempotency key
         (sessionId, seq); gaps in it read as lost batches during triage. */
      var out = { version: SCHEMA_VERSION, event: name, sessionId: SID,
                  atMs: Date.now(), seq: ++seq, device: deviceType() };
      for (var kk in kept) {
        if (Object.prototype.hasOwnProperty.call(kept, kk)) out[kk] = kept[kk];
      }
      return out;
    }

    function deviceType() {
      try {
        var wdt = w.innerWidth || 1024;
        return wdt < 768 ? 'mobile' : (wdt < 1180 ? 'tablet' : 'desktop');
      } catch (e) { return 'desktop'; }
    }

    /* ================================================================= queue */

    function track(name, fields) {
      try {
        if (!SID) return;
        if (sent >= cfg.maxEventsPerSession) return;   // hard cap
        var ev = validate(name, fields);
        if (!ev) return;
        if (queue.length >= cfg.maxQueue) queue.shift();  // drop OLDEST, never grow
        queue.push(ev);
        sent++;
        if (cfg.debug && w.console) w.console.log('[sl-telemetry]', ev);
        schedule();
      } catch (e) { /* swallowed by design */ }
    }

    function schedule() {
      try {
        if (timer || !live()) return;
        timer = w.setTimeout(function () { timer = null; flush(false); }, cfg.flushIntervalMs);
      } catch (e) {}
    }

    /**
     * Delivery is `sendBeacon` ONLY.
     *
     * It is fire-and-forget by specification: the browser queues it and the
     * page is free to close. There is no promise to await, no response to
     * read, and no error to handle — which is exactly why it is the only
     * transport used here. A failed beacon drops the batch. It is never
     * retried and never written to disk, because at-most-once delivery of
     * analytics is worth far less than a builder that never stalls.
     */
    function flush(isUnload) {
      try {
        if (!live() || !queue.length) return;
        if (!w.navigator || typeof w.navigator.sendBeacon !== 'function') { queue.length = 0; return; }
        var batch = { version: SCHEMA_VERSION, sessionId: SID, events: queue.slice() };
        queue.length = 0;
        var blob;
        /* text/plain is a CORS-safelisted content type, so the cross-origin
           beacon is a SIMPLE request and triggers no preflight OPTIONS — the
           collector needs no CORS handling, exactly as designed. The body is
           still the JSON batch; the collector parses the raw body as JSON and
           never inspects Content-Type. Do NOT use application/json here: it is
           not safelisted, forces a preflight, and the beacon would be dropped. */
        try { blob = new w.Blob([JSON.stringify(batch)], { type: 'text/plain;charset=UTF-8' }); }
        catch (e) { return; }
        w.navigator.sendBeacon(cfg.endpoint, blob);
      } catch (e) {}
      if (isUnload) { /* nothing further; the page is going away */ }
    }

    /* ====================================================== passive listeners */

    function on(target, type, fn) {
      try {
        target.addEventListener(type, function (e) {
          /* Every listener body is itself wrapped: a throw here would surface
             as an unhandled error inside a builder event handler. */
          try { fn(e); } catch (err) {}
        }, { passive: true, capture: true });
      } catch (e) {}
    }

    /* ---- diagnostics: errors the builder already raises ------------------- */
    on(w, 'error', function () { track('javascript_error', { code: 'script_error' }); });
    on(w, 'unhandledrejection', function () { track('javascript_error', { code: 'promise_rejection' }); });

    /* ---- builder load time, from Navigation Timing ------------------------ */
    function recordLoadTime() {
      try {
        var nav = (w.performance && w.performance.getEntriesByType)
          ? w.performance.getEntriesByType('navigation')[0] : null;
        var ms = nav ? Math.round(nav.domContentLoadedEventEnd) : null;
        if (ms !== null && isFinite(ms) && ms >= 0) {
          track('builder_load_time', { ms: ms });
        }
      } catch (e) {}
    }

    /* ---- upload: a file input change is the only reliable signal ---------- */
    function bucketFor(bytes) {
      if (!isFinite(bytes)) return null;
      var mb = bytes / (1024 * 1024);
      if (mb < 1) return '<1mb';
      if (mb < 5) return '1-5mb';
      if (mb < 20) return '5-20mb';
      return '>20mb';
    }
    function extFor(name) {
      try {
        var m = /\.([a-z0-9]{1,5})$/i.exec(String(name || ''));
        if (!m) return 'other';
        var e = m[1].toLowerCase();
        return ENUMS.fileType.indexOf(e) === -1 ? 'other' : e;
      } catch (err) { return 'other'; }
    }
    var uploadAttempt = 0;
    /* The started event's shape, held so the OUTCOME can be reported with the
       same fileType and size bucket without the builder having to pass them
       back — and so the two events are guaranteed to describe one attempt. */
    var uploadOpen = null;

    on(d, 'change', function (e) {
      var t = e && e.target;
      if (!t || t.tagName !== 'INPUT' || t.type !== 'file') return;
      var f = t.files && t.files[0];
      if (!f) return;
      uploadAttempt++;
      /* The NAME is never sent — only the extension and a size bucket. */
      var shape = {
        fileType: extFor(f.name),
        fileSizeBucket: bucketFor(f.size) || undefined,
        attemptNo: uploadAttempt
      };
      uploadOpen = { shape: shape, startedAt: Date.now() };
      track('upload_started', shape);
    });

    /**
     * THE OUTCOME OF AN UPLOAD, which nothing has ever reported.
     *
     * Until now the only upload signal was `upload_started`. Two of them exist
     * in the whole collection and both are desktop, which tells you an upload
     * BEGAN and nothing else — not whether the file was readable, not whether
     * the builder ever got the bytes. When order #1012 arrived with no artwork
     * there was no way to tell from telemetry whether the upload had failed or
     * had never been attempted at all. That ambiguity cost hours.
     *
     * Three states, now distinguishable:
     *   upload_started, no outcome   the browser died mid-read
     *   upload_failed                the file was rejected or unreadable
     *   upload_completed             the builder holds the original bytes
     *
     * And one more, from the events either side of it: `upload_completed` with
     * no following `add_to_cart` means the durable-asset attach refused the
     * checkout — which is the guard working, and is exactly what should have
     * happened for #1012.
     *
     * Deliberately reuses `upload_completed` / `upload_failed`, which the
     * DEPLOYED collector already validates. A new event name would be dropped
     * as `unknown_event` by the collector currently running, producing an
     * instrumented-looking path that silently records nothing — the same class
     * of failure this whole milestone exists to remove.
     *
     * Never carries a filename, a URL, or any part of the artwork.
     *
     * @param {boolean} ok
     * @param {string} [reason] one of ENUMS.reason; only read when ok is false
     */
    function uploadOutcome(ok, reason) {
      /* No open attempt means no file input was touched — a programmatic or
         hand-off path. Reporting an outcome for an attempt that was never
         started would put an orphan in the funnel. */
      if (!uploadOpen) return;
      var open = uploadOpen;
      uploadOpen = null;

      var fields = {
        fileType: open.shape.fileType,
        fileSizeBucket: open.shape.fileSizeBucket,
        attemptNo: open.shape.attemptNo
      };
      if (ok) {
        var ms = Date.now() - open.startedAt;
        if (isFinite(ms) && ms >= 0) fields.durationMs = Math.round(ms);
        track('upload_completed', fields);
      } else {
        fields.reason = reason || 'unknown';
        track('upload_failed', fields);
      }
    }

    /* ---- delegated clicks on the stable .pb-* contract -------------------- */
    var CLICK_MAP = [
      ['pb-addcart',  'add_to_cart'],
      ['pb-checkout', 'checkout_started'],
      ['pb-etool',    'crop_changed'],
      ['pb-ctool',    'zoom_changed']
    ];
    function classOf(el) {
      try { return ' ' + (el.className && el.className.baseVal !== undefined
        ? el.className.baseVal : String(el.className || '')) + ' '; }
      catch (e) { return ' '; }
    }
    on(d, 'click', function (e) {
      var el = e && e.target;
      for (var hops = 0; el && hops < 6; hops++, el = el.parentElement) {
        var cls = classOf(el);
        for (var i = 0; i < CLICK_MAP.length; i++) {
          if (cls.indexOf(' ' + CLICK_MAP[i][0] + ' ') !== -1) {
            track(CLICK_MAP[i][1]);
            return;
          }
        }
      }
    });

    /* ---- the cart add is the builder's ONLY fetch, so it is the one
            reliable place to observe an API failure ----------------------- */
    function wrapFetch() {
      try {
        if (typeof w.fetch !== 'function' || w.fetch.__slWrapped) return;
        var original = w.fetch;
        var wrapped = function (input, init) {
          var url = '';
          try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
          var isCart = url.indexOf('/cart/') !== -1;
          var started = Date.now();
          /* The original call is returned UNTOUCHED. Telemetry only observes
             the settled promise; it never delays, transforms or swallows. */
          var p = original.apply(this, arguments);
          try {
            if (isCart && p && typeof p.then === 'function') {
              p.then(function (res) {
                try {
                  if (res && !res.ok) {
                    track('api_error', { endpoint: 'cart_add', httpStatus: res.status });
                  }
                  track('interaction_latency', { ms: Date.now() - started });
                } catch (e) {}
                return res;
              }, function (err) {
                try { track('api_error', { endpoint: 'cart_add', httpStatus: 0 }); } catch (e) {}
                /* The rejection is NOT handled here — returning a rejected
                   promise preserves the builder's own error path exactly. */
                throw err;
              });
            }
          } catch (e) {}
          return p;
        };
        wrapped.__slWrapped = true;
        w.fetch = wrapped;
      } catch (e) {}
    }

    /* ---- lifecycle ------------------------------------------------------- */
    on(w, 'pagehide', function () { track('builder_closed'); flush(true); });
    on(d, 'visibilitychange', function () {
      try { if (d.visibilityState === 'hidden') flush(true); } catch (e) {}
    });

    /* ============================================================== public */

    /**
     * The seam for the builder to emit richer events when the bundle is next
     * rebuilt — size_selected, price_viewed, upload_completed/failed, undo and
     * reset all need state this file cannot observe from outside.
     *
     * Deliberately the same validate/queue path, so a builder-side caller
     * cannot bypass the allow-list or the caps.
     */
    w.slTrack = function (name, fields) { track(name, fields); };
    w.slTelemetry = {
      version: SCHEMA_VERSION,
      sessionId: function () { return SID; },
      enabled: function () { return live(); },

      /* The upload outcome seam. The builder calls this because only the
         builder knows whether the bytes actually arrived; this file owns the
         attempt number and the start time so the pair cannot disagree.
         Wrapped like everything else — an outcome call must never be able to
         break an upload. */
      uploadOutcome: function (ok, reason) {
        try { uploadOutcome(ok, reason); } catch (e) {}
      },

      /* ---- the two business-outcome seams ------------------------------
         NAMED seams, deliberately, and not a generic `track`. The builder
         bundle is forbidden from calling `slTrack` — its `.pb-*` classes are
         the QA screenshot contract and instrumentation stays outside it — so
         the sanctioned shape is exactly what `uploadOutcome` established: a
         method per fact, with a signature, so the bundle names an OUTCOME and
         this file decides what that means on the wire.

         The difference matters. A generic `track` in the bundle would let any
         future edit invent an event name with no reviewer and no vocabulary;
         these two can only ever say the one thing they are named for.

         Both are total: an unknown or missing dimension is normalised here
         rather than at the call site, so a caller cannot emit an outcome that
         the collector will refuse. */
      artworkAvailable: function (entryPath, surface) {
        try { track('artwork_available', { entryPath: entryPath, surface: surface }); } catch (e) {}
      },
      cartAddCompleted: function (entryPath, surface) {
        try { track('cart_add_completed', { entryPath: entryPath, surface: surface }); } catch (e) {}
      },

      /**
       * Why turn-on is verifiable instead of hopeful.
       *
       * "Is telemetry on?" has five independent answers — the switch, the
       * endpoint, whether that endpoint is acceptable, whether a session was
       * minted, and whether the browser can even send. Flipping the switch
       * and watching a dashboard that stays empty tells you nothing about
       * WHICH of the five is missing.
       *
       * `blockedBy` names them. Every value is a fixed token; nothing here
       * echoes the endpoint or any collected data back to the page.
       */
      readiness: function () {
        try {
          var blocked = [];
          if (cfg.enabled !== true) blocked.push('kill_switch_off');
          if (!cfg.endpoint) blocked.push('endpoint_not_set');
          else if (!ENDPOINT_OK) blocked.push('endpoint_not_https');
          if (!SID) blocked.push('no_session');
          var transport = !!(w.navigator && typeof w.navigator.sendBeacon === 'function');
          if (!transport) blocked.push('no_sendbeacon');
          return {
            version: SCHEMA_VERSION,
            enabled: cfg.enabled === true,
            endpointConfigured: !!cfg.endpoint,
            endpointValid: ENDPOINT_OK,
            transport: transport,
            session: !!SID,
            ready: blocked.length === 0,
            blockedBy: blocked
          };
        } catch (e) {
          return { ready: false, blockedBy: ['introspection_failed'] };
        }
      },

      /* Exposed for the GA4 `sl_session_id` custom dimension. */
      _queueLength: function () { return queue.length; },
      _flush: function () { flush(false); }
    };

    /* ---- start ----------------------------------------------------------- */
    wrapFetch();
    track('builder_opened');
    if (d.readyState === 'complete') recordLoadTime();
    else on(w, 'load', recordLoadTime);

  } catch (outer) {
    /* Setup itself failed. There is nothing to clean up and nothing to
       report — the builder continues exactly as though this file were not
       on the page. */
  }
})(window, document);
